import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, Phone } from "lucide-react";
import { COMPANY, WHATSAPP_URL } from "../lib/content";
import { AnimatePresence, motion } from "framer-motion";

const NAV = [
  { label: "Início", to: "/" },
  {
    label: "Institucional",
    to: "/quem-somos",
    children: [
      { label: "Quem somos", to: "/quem-somos" },
      { label: "Missão, Visão e Valores", to: "/quem-somos#mvv" },
      { label: "Diferenciais", to: "/quem-somos#diferenciais" },
      { label: "Metodologia", to: "/metodologia-de-obra" },
    ],
  },
  {
    label: "Serviços",
    to: "/servicos",
    children: [
      { label: "Obras Industriais", to: "/servicos/obras-industriais" },
      { label: "Reformas Industriais", to: "/servicos/reformas-industriais" },
      { label: "Obras Comerciais", to: "/servicos/obras-comerciais" },
      { label: "Adequações Estruturais", to: "/servicos/adequacoes-estruturais" },
      { label: "Obras Residenciais", to: "/servicos/obras-residenciais" },
      { label: "Gerenciamento de Obras", to: "/servicos/gerenciamento-de-obras" },
    ],
  },
  {
    label: "Projetos",
    to: "/projetos",
    children: [
      { label: "Obras Industriais", to: "/projetos/industriais" },
      { label: "Obras Comerciais", to: "/projetos/comerciais" },
      { label: "Reformas", to: "/projetos/reformas" },
      { label: "Cases Técnicos", to: "/projetos/cases-tecnicos" },
    ],
  },
  { label: "Notícias", to: "/noticias" },
  { label: "Contato", to: "/contato" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setHovered(null);
  }, [location.pathname]);

  return (
    <>
      {/* Top utility bar */}
      <div
        data-testid="header-utility-bar"
        className="hidden md:block w-full text-[11px] tracking-[0.22em] uppercase font-semibold bg-sca-black text-white/85"
      >
        <div className="sca-container flex items-center justify-between py-2.5">
          <div className="flex items-center gap-6">
            <span>Engenharia organizada.</span>
            <span className="hidden lg:inline text-white/40">— Piracicaba / SP</span>
          </div>
          <div className="flex items-center gap-6">
            <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-sca-red transition-colors flex items-center gap-2">
              <Phone className="w-3 h-3" /> {COMPANY.phone}
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="hover:text-sca-red transition-colors hidden lg:inline"
              data-testid="header-email-link"
            >
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-xl text-sca-black border-b border-sca-line shadow-[0_1px_0_rgba(0,0,0,0.02)]"
        onMouseLeave={() => setHovered(null)}
      >
        <div className="sca-container flex items-center justify-between h-[72px] md:h-[88px]">
          <Link
            to="/"
            data-testid="logo-home-link"
            className="flex items-center gap-3 group"
            aria-label="SCA Construtora — Início"
          >
            <img
              src={COMPANY.logos.positive}
              alt="SCA Construtora"
              className="h-10 md:h-12 w-auto select-none"
              draggable={false}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Navegação principal">
            {NAV.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setHovered(item.children ? item.label : null)}
              >
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block px-4 py-7 text-[12px] font-semibold tracking-[0.18em] uppercase transition-colors ${
                      isActive ? "text-sca-red" : "text-sca-black hover:text-sca-red"
                    }`
                  }
                >
                  {item.label}
                </NavLink>

                {/* Mega menu dropdown — covers the gap via padding-top */}
                <AnimatePresence>
                  {item.children && hovered === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute left-0 top-full min-w-[300px]"
                    >
                      <div className="bg-white border border-sca-line shadow-[0_24px_60px_-12px_rgba(7,7,7,0.18)]">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-sub-${c.to}`}
                            className="flex items-center justify-between gap-6 px-5 py-4 text-[13px] font-medium text-sca-black hover:bg-sca-mist hover:text-sca-red transition-colors border-b border-sca-line/60 last:border-b-0"
                          >
                            <span>{c.label}</span>
                            <ChevronRight className="w-4 h-4 opacity-50" />
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* CTA + Mobile toggle */}
          <div className="flex items-center gap-3">
            <a
              href={WHATSAPP_URL()}
              target="_blank"
              rel="noreferrer"
              data-testid="header-cta-whatsapp"
              className="hidden md:inline-flex items-center gap-2 px-5 lg:px-7 py-3 text-[11px] font-bold tracking-[0.22em] uppercase bg-sca-red text-white hover:bg-sca-red-dark transition-colors"
            >
              Avaliação técnica
            </a>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Fechar menu" : "Abrir menu"}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-sca-black"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden bg-white border-t border-sca-line text-sca-black"
              data-testid="mobile-menu-panel"
            >
              <nav className="sca-container py-2 flex flex-col">
                {NAV.map((item) => (
                  <div key={item.label} className="border-b border-sca-line/70">
                    <NavLink
                      to={item.to}
                      end={item.to === "/"}
                      className="block py-1.5 text-[12px] font-semibold tracking-[0.14em] uppercase hover:text-sca-red"
                    >
                      {item.label}
                    </NavLink>
                    {item.children && (
                      <div className="pb-1.5 pl-1">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            className="block py-0.5 text-[11.5px] text-sca-steel hover:text-sca-red"
                          >
                            — {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <a
                  href={WHATSAPP_URL()}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 mb-2 btn-sca-primary justify-center py-2.5"
                  data-testid="mobile-cta-whatsapp"
                >
                  Avaliação técnica
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
